/* eslint-disable @next/next/no-img-element */
// SEÇÃO "NOSSA EMPRESA" COM FAIXA (modelo_empresa = 3) — a etapa institucional do
// MODELO Autoral (referência mikecorretorimobiliario.com.br).
//
// O que muda em relação à variante legada (OwnersPieces): a ARTE e o TEXTO deixam de
// ser dois blocos soltos em pontos distintos da home e passam a ser UM bloco só — a
// imagem sangra de ponta a ponta da página (largura total, proporção natural, sem
// crop nem escurecimento) e o texto vem colado logo abaixo, em colunas de leitura.
// Por isso, quando esta variante está ligada, o slot `banner` da home não renderiza:
// a arte foi MOVIDA para cá, não duplicada (ver pages/index.tsx, slot "banner").
//
// TUDO VARIÁVEL, zero campo novo: a arte é o banner "home" do tenant (aba Banners)
// com queda para o banner da própria seção (`linkbanner`, em Banner Home do CRM);
// título/subtítulos/parágrafos são os mesmos campos que a variante legada já usa; a
// cor de fundo segue o `corconfig` da seção e a cor do texto é CALCULADA sobre ela
// (`ensureReadableTextColor`) — nunca chutada.
// Default export = ErrorBoundary com fallback (regra 3 do registry/README).
import React from 'react';
import Image from 'next/image';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import { anuncieWhatsAppDoSite } from '../../../utils/anuncieWhatsApp';
import type { EmpresaSectionProps } from '../../registry/types';
import { textosSecoesDoSite, tituloEmFrase } from '../../../utils/textosSecoesPorSite';
import styles from './EmpresaAutoral.module.css';

/** Primeira URL utilizável de um banner que chega como array, objeto ou string. */
const urlDoBanner = (banner: any): string => {
    const item = Array.isArray(banner) ? banner.find((b: any) => b?.url) : banner;
    const url = typeof item === 'string' ? item : item?.url;
    return url ? String(url).replaceAll('"', '') : '';
};

const EmpresaAutoralInner: React.FC<EmpresaSectionProps> = ({
    allOwners,
    site,
    banner,
    bannerMobile,
    setFullScram,
    origin = '',
    path = '',
}) => {
    // A arte é o banner "home" do tenant (o que o slot `banner` mostrava solto no meio
    // da home); sem ele, cai no banner da própria seção, que o CRM já edita.
    const imagem = urlDoBanner(banner) || urlDoBanner(allOwners?.linkbanner);
    // Arte específica de celular ("home_mobile"), quando o tenant cadastrou — o mesmo
    // banner que o slot `banner` já empilhava por dispositivo. Sem ela, a arte de
    // desktop é reenquadrada no celular (.arteCorte): uma faixa 6:1 renderizada
    // inteira num telefone vira uma tira de 60px onde não se lê a mensagem.
    const imagemMobile = urlDoBanner(bannerMobile);

    // Mesma régua de cor da variante legada (OwnersPieces): o `corconfig` da seção
    // escolhe o fundo, e a cor do texto é derivada dele.
    const corFundo =
        allOwners?.corconfig == 1 ? site?.cor1
        : allOwners?.corconfig == 2 ? site?.cor2
        : allOwners?.corconfig == 3 ? '#000000'
        : allOwners?.corconfig == 4 ? site?.cor1
        : allOwners?.corconfig == 5 ? site?.cor5
        : allOwners?.corconfig == 6 ? site?.cor3
        : '#ffffff';
    const fundoClaro = isColorLight(corFundo);
    // Texto: preferimos a cor cadastrada do tenant para o papel (cor11 em fundo claro,
    // cor13 em fundo escuro) e só a aceitamos se ela PASSA no contraste sobre o fundo.
    const corTexto = ensureReadableTextColor(fundoClaro ? site?.cor11 : site?.cor13, corFundo);
    const corTitulo = ensureReadableTextColor(fundoClaro ? site?.cor2 : site?.cor13, corFundo, 3);
    const corBotao = ensureReadableTextColor(site?.cor2, corFundo, 3);
    const corTextoBotao = isColorLight(corBotao) ? '#111823' : '#ffffff';

    const blocos = [
        { subtitulo: allOwners?.subtitulo_1, paragrafo: allOwners?.paragrafo_1 },
        { subtitulo: allOwners?.subtitulo_2, paragrafo: allOwners?.paragrafo_2 },
        { subtitulo: allOwners?.subtitulo_3, paragrafo: allOwners?.paragrafo_3 },
    ].filter((b) => b.subtitulo || b.paragrafo);

    if (!imagem && !blocos.length && !allOwners?.card_titulo) return null;

    const abrirSimulador = () => {
        if (setFullScram) setFullScram(1);
    };

    const abrirWhatsapp = (e: React.MouseEvent) => {
        e.preventDefault();
        const texto = 'Olá, gostaria de anunciar meu imóvel';
        const telefone = anuncieWhatsAppDoSite(site) ?? `55${(site?.Dados?.tel1 || site?.Dados?.tel2)?.replace(/\D/g, '')}`;
        const url = `https://api.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(texto)} Link: ${origin}${path}`;
        setTimeout(() => {
            window.open(url, '_blank');
        }, 300);
    };

    return (
        <section
            className={styles.secao}
            data-builder-section="sobre"
            style={{ backgroundColor: corFundo, color: corTexto }}
        >
            {imagem ? (
                <div className={styles.faixa}>
                    {/* Arte com texto: quality 90 (o default 75 borra a tipografia do banner — #748).
                        As duas versões coexistem escondidas por media query; como ambas são
                        `lazy`, a que está em `display:none` nunca entra no viewport e não é
                        baixada — o celular puxa só a arte dele. */}
                    {imagemMobile ? (
                        <Image
                            className={`${styles.arte} ${styles.arteMobile}`}
                            src={imagemMobile}
                            alt=""
                            aria-hidden="true"
                            width={1080}
                            height={600}
                            sizes="100vw"
                            quality={90}
                            loading="lazy"
                        />
                    ) : null}
                    <Image
                        className={`${styles.arte} ${imagemMobile ? styles.arteDesktop : styles.arteCorte}`}
                        src={imagem}
                        alt=""
                        aria-hidden="true"
                        width={1920}
                        height={300}
                        sizes="100vw"
                        quality={90}
                        loading="lazy"
                    />
                </div>
            ) : null}

            <div className={styles.conteudo}>
                {allOwners?.card_titulo ? (
                    <h2 className={styles.titulo} style={{ color: corTitulo }}>
                        {/* caixa alta de sempre; quem pediu títulos em frase (cs2639) recebe "Quem somos" */}
                        {textosSecoesDoSite(site?.id).titulosEmFrase
                            ? tituloEmFrase(allOwners.card_titulo)
                            : String(allOwners.card_titulo).toUpperCase()}
                        <span className={styles.regua} style={{ backgroundColor: corTitulo }} aria-hidden="true" />
                    </h2>
                ) : null}

                {blocos.length ? (
                    <div className={styles.blocos}>
                        {blocos.map((bloco, i) => (
                            <article className={styles.bloco} key={`empresa-bloco-${i}`}>
                                {bloco.subtitulo ? (
                                    <h3 className={styles.subtitulo} style={{ color: corTitulo }}>
                                        {bloco.subtitulo}
                                    </h3>
                                ) : null}
                                {bloco.paragrafo ? <p className={styles.paragrafo}>{bloco.paragrafo}</p> : null}
                            </article>
                        ))}
                    </div>
                ) : null}

                <div className={styles.ctas}>
                    <button
                        type="button"
                        className={styles.botaoPrimario}
                        onClick={abrirWhatsapp}
                        style={{ backgroundColor: corBotao, color: corTextoBotao }}
                    >
                        Anuncie Seu Imóvel
                    </button>
                    {site?.simulador ? (
                        <button
                            type="button"
                            className={styles.botaoSecundario}
                            onClick={abrirSimulador}
                            style={{ borderColor: corBotao, color: corBotao }}
                        >
                            Simular Financiamento
                        </button>
                    ) : null}
                </div>
            </div>
        </section>
    );
};

class EmpresaAutoral extends React.Component<EmpresaSectionProps, { hasError: boolean }> {
    constructor(props: EmpresaSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro fallback sem derrubar a home.
    }
    render() {
        const { fallback, site, allOwners } = this.props;
        if (this.state.hasError || !site || !allOwners) return fallback ?? null;
        return <EmpresaAutoralInner {...this.props} />;
    }
}

export default EmpresaAutoral;
